import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Info, TriangleAlert } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";
import { useAgentList } from "~/hooks/useAgentList";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { nextgenAdminEvalRunPath } from "~/pages/manifold-nextgen/paths";
import { AgentListView, AgentStatus, type SelectableModel, modelsPath } from "~/services/agents";
import {
  evalRunLaunchPath,
  type EvalRun,
  type EvalRunLaunch,
  type PartialEvalCase,
} from "~/services/evals";
import { apiMutate } from "~/utils/apiClient";
import { POPOVER_IN_DIALOG_Z } from "./constants/page";
import {
  LAUNCH_CONCURRENT_RUN_CAP,
  LAUNCH_DEFAULTS,
  LAUNCH_MAX_CONCURRENCY,
  LAUNCH_MAX_ITERATIONS,
} from "./constants/runs";
import { Field } from "./primitives/layout";
import { type LaunchTagOption, casesSelectedByTags, launchTagOptions } from "./selectors/facets";

/**
 * Select sentinel for "don't override — keep the suite's default judge model".
 * Radix `Select` has no empty value, so the absence of an override is its own
 * option; a field left here sends no key, which is how the runner keeps the
 * default. Not a model name, so it can never collide with one.
 */
const JUDGE_SUITE_DEFAULT = "__suite_default__";

interface LaunchRunDialogProps {
  onOpenChange: (open: boolean) => void;
  /**
   * Every case the caller can read. Selected out of rather than passed as a tag
   * list, because the count a tag resolves to is what makes the selector legible —
   * and the runner reads the same list, so the count shown is the count that runs.
   */
  cases: PartialEvalCase[];
  /** Tags the page is already filtered to, pre-selected so the dialog opens in context. */
  initialTags: string[];
  onLaunched: () => void;
}

/**
 * Configures and launches a run. `POST /eval-runs/launch` both records the run and
 * starts the ECS task, so this is the one control in the UI that spends real agent
 * capacity — hence a form built around what a launch costs.
 *
 * Mounted only while open (see `EvalsPage`), so every field resets between
 * launches and `initialTags` is read when the dialog opens, not at page load.
 */
export const LaunchRunDialog = ({
  onOpenChange,
  cases,
  initialTags,
  onLaunched,
}: LaunchRunDialogProps) => {
  const navigate = useNavigate();
  // `usable` rather than `all`: a launch requires `can_use`, so offering an agent
  // the caller cannot use only produces a 404 on submit. The whole list, not the
  // capped filter page — the agent is required, so one past a 20-row cap is a run
  // nobody can launch. DISABLED drafts are dropped here instead, which is what the
  // capped hook's server-side status filter had bought.
  const { data: agentList } = useAgentList(true, AgentListView.USABLE);
  // Sorted on what the menu shows. The paged list comes back ordered by `name`,
  // and the rows read `display_name`, which is a different string.
  const agents = (agentList?.items ?? [])
    .filter((agent) => agent.status === AgentStatus.ENABLED)
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  const [agentId, setAgentId] = useState("");
  const [tags, setTags] = useState<string[]>(initialTags);
  const [iterations, setIterations] = useState(String(LAUNCH_DEFAULTS.iterations));
  const [maxConcurrency, setMaxConcurrency] = useState(String(LAUNCH_DEFAULTS.max_concurrency));
  const [rewriteQueries, setRewriteQueries] = useState<boolean>(LAUNCH_DEFAULTS.rewrite_queries);
  const [keepConversations, setKeepConversations] = useState<boolean>(
    LAUNCH_DEFAULTS.keep_conversations
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [figureJudgeModel, setFigureJudgeModel] = useState(JUDGE_SUITE_DEFAULT);
  const [textJudgeModel, setTextJudgeModel] = useState(JUDGE_SUITE_DEFAULT);
  const [figureInstructions, setFigureInstructions] = useState("");
  const [textInstructions, setTextInstructions] = useState("");

  // The same curated catalog the agent behavior picker uses. Judge models draw
  // from one source of truth as orchestrator models; the runner resolves a
  // `ModelName` to its provider string exactly as an agent's model is resolved.
  const { data: judgeModels } = useFetcher<SelectableModel[]>(modelsPath());
  const modelOptions = judgeModels ?? [];

  // Bounded above too: concurrency multiplies the agent turns a run makes at
  // once, so an extra digit is a launch that spends far more than was meant.
  const parsedConcurrency = Number(maxConcurrency);
  const isConcurrencyValid =
    Number.isInteger(parsedConcurrency) &&
    parsedConcurrency >= 1 &&
    parsedConcurrency <= LAUNCH_MAX_CONCURRENCY;

  // Same bounding as concurrency: iterations multiply the conversations a run
  // makes, so an out-of-range value is a launch that spends far more than meant.
  const parsedIterations = Number(iterations);
  const isIterationsValid =
    Number.isInteger(parsedIterations) &&
    parsedIterations >= 1 &&
    parsedIterations <= LAUNCH_MAX_ITERATIONS;

  const tagOptions = launchTagOptions(cases, tags);
  const selectedCases = casesSelectedByTags(cases, tags);
  // A launch that selects nothing is rejected by the runner rather than recorded
  // as an empty run, so the combination is blocked here instead of failing after
  // the task has started. Only decidable once there are cases to select from: an
  // empty list is the dashboard still loading, not a bad selection.
  const selectsNothing = cases.length > 0 && selectedCases.length === 0;

  const { execute, isPending } = useAsyncMutation(
    async () => {
      // Only non-empty overrides are sent: the API rejects a null or empty
      // value for a judge key rather than treating it as "keep the default",
      // because an ignored key would leave the scores looking authoritative
      // while the rubric never changed.
      const judgeConfig: Record<string, string> = {};
      if (figureJudgeModel !== JUDGE_SUITE_DEFAULT) {
        judgeConfig.figure_judge_model = figureJudgeModel;
      }
      if (textJudgeModel !== JUDGE_SUITE_DEFAULT) {
        judgeConfig.text_response_judge_model = textJudgeModel;
      }
      if (figureInstructions.trim()) {
        judgeConfig.figure_judging_instructions = figureInstructions.trim();
      }
      if (textInstructions.trim()) {
        judgeConfig.text_response_judging_instructions = textInstructions.trim();
      }

      const body: EvalRunLaunch = {
        agent_id: agentId,
        tags,
        run_parameters: {
          iterations: parsedIterations,
          max_concurrency: parsedConcurrency,
          rewrite_queries: rewriteQueries,
          keep_conversations: keepConversations,
        },
        ...(Object.keys(judgeConfig).length > 0 ? { judge_config: judgeConfig } : {}),
      };
      return apiMutate<EvalRun>(evalRunLaunchPath(), "POST", body);
    },
    {
      errorMessage: `Could not launch the run. The eval runner may be disabled here, or the customer may already have ${LAUNCH_CONCURRENT_RUN_CAP} runs in flight.`,
      onSuccess: async (run) => {
        onOpenChange(false);
        onLaunched();
        navigate(nextgenAdminEvalRunPath(run.id));
      },
    }
  );

  return (
    <FormDialog
      open
      onOpenChange={onOpenChange}
      title="Launch eval run"
      description="Results appear as they finish."
      contentClassName="sm:max-w-xl"
      primaryLabel="Launch run"
      primaryPendingLabel="Launching…"
      primaryDisabled={!agentId || !isIterationsValid || !isConcurrencyValid || selectsNothing}
      isPending={isPending}
      onPrimary={() => void execute()}
    >
      <div className="-mr-2 max-h-[62vh] overflow-y-auto pr-2">
        <div className="flex flex-col gap-6 py-2">
          <Field
            label="Agent"
            hint="The run evaluates this agent, and inherits its orchestrator model."
          >
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="w-full" aria-label="Agent to evaluate">
                <SelectValue placeholder="Select an agent…" />
              </SelectTrigger>
              <SelectContent className={POPOVER_IN_DIALOG_Z}>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <TagPicker
            options={tagOptions}
            totalCaseCount={cases.length}
            selectedCaseCount={selectedCases.length}
            onToggle={(tag) =>
              setTags((current) =>
                current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]
              )
            }
            onClear={() => setTags([])}
          />

          <Separator />

          <div className="flex flex-wrap items-start gap-x-10 gap-y-5">
            <Field
              label="Iterations"
              hint={`Each case is asked this many times, up to ${LAUNCH_MAX_ITERATIONS}.`}
            >
              <Input
                type="number"
                min={1}
                max={LAUNCH_MAX_ITERATIONS}
                value={iterations}
                onChange={(event) => setIterations(event.target.value)}
                aria-label="Iterations per case"
                aria-invalid={!isIterationsValid || undefined}
                className="w-24 tabular-nums"
              />
            </Field>

            <Field
              label="Max concurrency"
              hint={`Cases evaluated in parallel, up to ${LAUNCH_MAX_CONCURRENCY}.`}
            >
              <Input
                type="number"
                min={1}
                max={LAUNCH_MAX_CONCURRENCY}
                value={maxConcurrency}
                onChange={(event) => setMaxConcurrency(event.target.value)}
                aria-label="Maximum concurrent cases"
                aria-invalid={!isConcurrencyValid || undefined}
                className="w-24 tabular-nums"
              />
            </Field>
          </div>

          <div className="flex flex-col gap-3">
            <ToggleRow
              label="Rewrite queries"
              hint="Paraphrases each query first, so scores reflect intent, not wording."
              checked={rewriteQueries}
              onCheckedChange={setRewriteQueries}
            />
            <ToggleRow
              label="Keep conversations"
              hint="Keeps each session, so a failure can be opened as a transcript."
              checked={keepConversations}
              onCheckedChange={setKeepConversations}
            />
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
              <ChevronDown
                className={cn("size-4 transition-transform", advancedOpen && "rotate-180")}
                aria-hidden
              />
              Judge overrides
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-4 pt-4">
              <p className="flex gap-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                Changing a judge model changes what the scores mean, so runs judged differently are
                not comparable. Keep &ldquo;Suite default&rdquo; to inherit the suite&rsquo;s model.
              </p>
              <JudgeOverride
                judge="Figure"
                placeholders="{query}, {skill_judging_block}"
                model={figureJudgeModel}
                onModelChange={setFigureJudgeModel}
                instructions={figureInstructions}
                onInstructionsChange={setFigureInstructions}
                models={modelOptions}
              />
              <JudgeOverride
                judge="Text response"
                placeholders="{query}, {expected_answer}"
                model={textJudgeModel}
                onModelChange={setTextJudgeModel}
                instructions={textInstructions}
                onInstructionsChange={setTextInstructions}
                models={modelOptions}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      <LaunchScale
        caseCount={selectedCases.length}
        iterations={isIterationsValid ? parsedIterations : null}
        concurrency={isConcurrencyValid ? parsedConcurrency : null}
        selectsNothing={selectsNothing}
        tagCount={tags.length}
      />
    </FormDialog>
  );
};

/**
 * The tag selector, as every tag at once with the cases each would leave. Flat
 * rather than behind a dropdown, because the rule is containment and nothing on a
 * collapsed trigger can express it: watching every other count drop when a second
 * tag goes on is what teaches it.
 */
const TagPicker = ({
  options,
  totalCaseCount,
  selectedCaseCount,
  onToggle,
  onClear,
}: {
  options: LaunchTagOption[];
  totalCaseCount: number;
  selectedCaseCount: number;
  onToggle: (tag: string) => void;
  onClear: () => void;
}) => {
  const selectedTagCount = options.filter((option) => option.selected).length;

  return (
    <fieldset className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <legend className="text-sm font-medium text-foreground">Tags</legend>
        {selectedTagCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="cursor-pointer text-xs text-muted-foreground hover:text-foreground"
          >
            Clear tags
          </button>
        )}
      </div>
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">No cases are tagged yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {options.map((option) => (
            <button
              key={option.tag}
              type="button"
              aria-pressed={option.selected}
              onClick={() => onToggle(option.tag)}
              className={cn(
                "flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
                option.selected
                  ? "border-primary bg-primary/10 font-medium text-primary"
                  : "border-input bg-background text-foreground hover:bg-accent",
                // A tag that would select nothing stays clickable — a reviewer
                // has to be able to take it back off again — but reads as the
                // dead end it is rather than as one more option.
                !option.selected && option.caseCount === 0 && "text-muted-foreground opacity-60"
              )}
            >
              <span className="max-w-[180px] truncate">{option.tag}</span>
              <span
                className={cn(
                  "tabular-nums",
                  option.selected ? "text-primary/70" : "text-muted-foreground"
                )}
              >
                {option.caseCount}
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {selectedTagCount === 0
          ? `No tags, so the run asks all ${totalCaseCount} cases you can read.`
          : selectedTagCount === 1
            ? `${selectedCaseCount} of ${totalCaseCount} cases carry this tag.`
            : `${selectedCaseCount} of ${totalCaseCount} cases carry all ${selectedTagCount} tags — a case needs every one of them.`}
      </p>
    </fieldset>
  );
};

/**
 * What the launch will spend, stated beside the button that spends it. Iterations
 * multiply cases, so neither number the reviewer set is the number about to run.
 * Outside the scrolling body, so it is on screen whenever the button is.
 */
const LaunchScale = ({
  caseCount,
  iterations,
  concurrency,
  selectsNothing,
  tagCount,
}: {
  caseCount: number;
  /** Null while the field holds something that is not a positive integer. */
  iterations: number | null;
  /** Null while the field holds something that is not a positive integer. */
  concurrency: number | null;
  selectsNothing: boolean;
  tagCount: number;
}) => {
  if (selectsNothing) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/40">
        <TriangleAlert
          className="mt-0.5 size-4 shrink-0 text-red-700 dark:text-red-400"
          aria-hidden
        />
        <p className="text-xs text-red-700 dark:text-red-400">
          No case carries all {tagCount} of these tags, so there is nothing to run. Drop one.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-md border border-border bg-muted/50 px-3 py-2">
      <span className="text-sm font-medium tabular-nums text-foreground">
        {iterations != null ? `${caseCount * iterations} agent conversations` : "—"}
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">
        {caseCount} {caseCount === 1 ? "case" : "cases"}
        {iterations != null && iterations > 1 && ` × ${iterations} iterations`}
        {concurrency != null && ` · ${concurrency} at a time`}
      </span>
    </div>
  );
};

/**
 * The per-run override for one judge: the model that scores it, and the rubric
 * it scores against. Models come from the same `GET /models` catalog the agent
 * behavior picker uses, so a judge runs on a model an orchestrator could.
 */
const JudgeOverride = ({
  judge,
  placeholders,
  model,
  onModelChange,
  instructions,
  onInstructionsChange,
  models,
}: {
  judge: string;
  placeholders: string;
  model: string;
  onModelChange: (value: string) => void;
  instructions: string;
  onInstructionsChange: (value: string) => void;
  models: SelectableModel[];
}) => (
  <>
    <Field label={`${judge} judge model`}>
      <Select value={model} onValueChange={onModelChange}>
        <SelectTrigger className="w-full" aria-label={`${judge} judge model`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className={POPOVER_IN_DIALOG_Z}>
          <SelectItem value={JUDGE_SUITE_DEFAULT}>Suite default</SelectItem>
          {models.map((option) => (
            <SelectItem key={option.name} value={option.name}>
              {option.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
    <Field
      label={`${judge} judging instructions`}
      hint={`Leave blank to keep the suite default. Placeholders: ${placeholders}.`}
    >
      <Textarea
        value={instructions}
        onChange={(event) => onInstructionsChange(event.target.value)}
        placeholder="Suite default"
        className="min-h-24 font-mono text-xs"
        aria-label={`${judge} judging instructions`}
      />
    </Field>
  </>
);

const ToggleRow = ({
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex flex-col gap-0.5">
      <Label className="text-sm font-medium">{label}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={label} />
  </div>
);
